import './styles/App.css';
import React, { useState, useEffect, useCallback, Suspense } from 'react';
import AccountLocked from './pages/AccountLocked';
import { jwtDecode } from 'jwt-decode';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TRANSFORM_USER } from './Transformations';
import { logout, ssoUserLogin } from './api/userDataService';
import { initAllAnimations } from './utils/scrollAnimations';
import { getTheme } from './api/themeService';
import './styles/theme.css';

// Import the visual message components (keep these - they're lightweight)
import { VisualMessageProvider, useVisualMessage } from './components/Validation/VisualMessageContext';
import VisualMessageContainer from './components/Validation/VisualMessageContainer';
import { TooltipProvider } from './contexts/TooltipContext';
import { AIAdvisorProvider } from './contexts/AIAdvisorContext';
import PersistentAIPlayer from './components/AI/PersistentAIPlayer';

// Import the inactivity timeout hook (keep this - it's lightweight)
import useInactivityTimeout from './utils/useInactivityTimeout';
import { setActivityResetCallback } from './api/userDataService';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Retry failed requests once
    },
  },
});

// LAZY LOADING: Convert page imports to lazy loaded components
const Login = React.lazy(() => import('./pages/Login'));
const Homepage = React.lazy(() => import('./pages/Homepage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Education = React.lazy(() => import('./pages/Education'));
const CV = React.lazy(() => import('./pages/CV'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Jobs = React.lazy(() => import('./pages/Jobs'));

// Loading Component - matches your existing design pattern
const LoadingSpinner = () => (
  <div className="loader flex items-center justify-center min-h-screen bg-white">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 md:h-16 md:w-16 border-4 border-gray-200 border-t-theme-primary mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium text-sm md:text-base">Loading...</p>
    </div>
  </div>
);

// AnimationWrapper component (unchanged)
const AnimationWrapper = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    const cleanup = initAllAnimations();
    window.scrollTo(0, 0);
    return () => typeof cleanup === 'function' && cleanup();
  }, [location.pathname]);
  
  return <>{children}</>;
};

function NotFound() {
  return (
    <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
      <h1 style={{ fontSize: '6rem', fontWeight: 800, color: '#008f3e', lineHeight: 1 }}>404</h1>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Page not found</h2>
      <p style={{ color: '#6b7280', marginBottom: '2rem' }}>The page you're looking for doesn't exist or has been moved.</p>
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <a href="/homepage" style={{ padding: '0.5rem 1.25rem', background: '#008f3e', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 600 }}>Go home</a>
      </div>
    </div>
  );
}

// Protected route guard - defined outside App to maintain stable component identity
// (defining inside App causes remount on every App re-render, losing child component state)
const Protected = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) return <Navigate to="/login" />;
  // Check paywall
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const paywalled = (u.accountStatus === 'alumni' || u.accountType === 'individual') && u.subscriptionStatus !== 'active';
    if (paywalled) return <Navigate to="/account-locked" />;
  } catch { /* proceed */ }
  return children;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const { showMessage } = useVisualMessage();

  // Inactivity timeout configuration (unchanged)
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
  const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

  const isDarkMode = localStorage.getItem('theme') === 'dark';

  const handleLogout = useCallback((showLogoutMessage = false) => {
    logout();
    setIsAuthenticated(false);
    
    if (showLogoutMessage) {
      showMessage("You have been logged out due to inactivity", "info");
    }
  }, [showMessage]);

  const handleInactivityTimeout = useCallback(() => {
    handleLogout(true);
  }, [handleLogout]);

  // Use the inactivity timeout hook only when authenticated (unchanged)
  const { resetTimeout } = useInactivityTimeout(
    isAuthenticated ? handleInactivityTimeout : () => {}, 
    INACTIVITY_TIMEOUT
  );

  // Set up API activity tracking (unchanged)
  useEffect(() => {
    if (isAuthenticated) {
      setActivityResetCallback(resetTimeout);
    } else {
      setActivityResetCallback(null);
    }
  }, [isAuthenticated, resetTimeout]);

  useEffect(() => {
  }, [isAuthenticated]);

  // SSO Login - check for sso_token in URL on mount
  useEffect(() => {
    const checkSSOToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const ssoToken = urlParams.get('sso_token');
      
      if (ssoToken) {
        try {
          console.log("SSO token found, attempting login...");
          const result = await ssoUserLogin(ssoToken);
          
          if (result && result.token && result.user) {
            const transformedUser = TRANSFORM_USER(result.user);
            localStorage.setItem('token', result.token);
            localStorage.setItem('user', JSON.stringify(transformedUser));
            setIsAuthenticated(true);
            
            // Dispatch custom event to notify TooltipContext of login
            window.dispatchEvent(new Event('userLogin'));
            
            resetTimeout();
            
            // Remove sso_token from URL
            urlParams.delete('sso_token');
            const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
            window.history.replaceState({}, '', newUrl);
            
            showMessage("SSO login successful!", "success");
          }
        } catch (error) {
          console.error("SSO login error:", error);
          showMessage("SSO login failed. Please try logging in manually.", "error");
          // Remove sso_token from URL even on error
          urlParams.delete('sso_token');
          const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', newUrl);
        }
      }
    };
    
    checkSSOToken();
  }, [resetTimeout, showMessage]);

  // Main authentication check - runs once on mount (unchanged)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now();
        const tokenExpiry = decoded.exp * 1000;
        
        if (tokenExpiry < currentTime) {
          logout();
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
          // Dispatch login event if already authenticated (user returning)
          window.dispatchEvent(new Event('userLogin'));
        }
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  // Load theme ONLY when user is authenticated (unchanged)
  useEffect(() => {
    if (isAuthenticated === true) {
      const loadTheme = async () => {
        try {
          await getTheme();
        } catch (error) {
        }
      };
      loadTheme();
    }
  }, [isAuthenticated]);

  // Token expiry warning - separate from main auth check (unchanged)
  useEffect(() => {
    if (isAuthenticated === true) {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now();
          const tokenExpiry = decoded.exp * 1000;
          const timeUntilExpiry = tokenExpiry - currentTime;
          
          if (timeUntilExpiry < WARNING_TIME && timeUntilExpiry > 0) {
            showMessage("Your session will expire soon. Please save your work.", "warning");
          }
        } catch (error) {
          // Token parsing error handled in main useEffect
        }
      }
    }
  }, [isAuthenticated, showMessage]);

  useEffect(() => {
    if (isDarkMode)
    {
      document.body.classList.add('dark-mode');
    }
  }, []);

  const handleLogin = async ({ token, refreshToken, user }) => {
    try {
      if (!token || !user) {
        throw new Error("Invalid login response structure");
      }
      const transformedUser = TRANSFORM_USER(user);
      localStorage.setItem('token', token); 
      localStorage.setItem('user', JSON.stringify(transformedUser));
      setIsAuthenticated(true);
      
      // Dispatch custom event to notify TooltipContext of login
      window.dispatchEvent(new Event('userLogin'));
      
      // Reset the inactivity timer on successful login
      resetTimeout();
      
      showMessage("Login successful!", "success");
    } catch (error) {
      console.error("Login Error:", error.message);
      showMessage("Login failed. Please try again.", "error");
    }
  };

  // Enhanced logout that provides feedback (unchanged)
  const handleManualLogout = useCallback(() => {
    handleLogout();
    showMessage("You have been logged out successfully", "success");
  }, [handleLogout, showMessage]);

  // Show loading while checking authentication (unchanged)
  if (isAuthenticated === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <AnimationWrapper>
      {/* SUSPENSE WRAPPER: This handles lazy loading fallbacks */}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route
            path="/login"
            element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/homepage" />}
          />
          <Route
            path="/account-locked"
            element={isAuthenticated ? <AccountLocked onLogout={handleManualLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/homepage"
            element={<Protected isAuthenticated={isAuthenticated}><Homepage onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/dashboard"
            element={<Protected isAuthenticated={isAuthenticated}><Dashboard onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/profile"
            element={<Protected isAuthenticated={isAuthenticated}><Profile onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/education"
            element={<Protected isAuthenticated={isAuthenticated}><Education onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/cv"
            element={<Protected isAuthenticated={isAuthenticated}><CV onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/settings"
            element={<Protected isAuthenticated={isAuthenticated}><Settings onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/jobs"
            element={<Protected isAuthenticated={isAuthenticated}><Jobs onLogout={handleManualLogout} /></Protected>}
          />
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? "/homepage" : "/login"} />}
          />
          <Route
            path="*"
            element={isAuthenticated ? <NotFound /> : <Navigate to="/login" />}
          />
        </Routes>
      </Suspense>

      <VisualMessageContainer />

      {/* Persistent AI Career Advisor - stays visible across pages */}
      {isAuthenticated && <PersistentAIPlayer />}
    </AnimationWrapper>
  );
}

function AppWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <VisualMessageProvider>
        <TooltipProvider>
          <AIAdvisorProvider>
            <Router>
              <App />
            </Router>
          </AIAdvisorProvider>
        </TooltipProvider>
      </VisualMessageProvider>
    </QueryClientProvider>
  );
}

export default AppWrapper;